/**
 * NotifPrefsPanel — per-user notification preferences popover.
 *
 * Wired into the Conv v2 header (and reusable elsewhere via `trigger`).
 * Lets every user — including external invitees — control their own
 * notification experience without an admin settings page:
 *   • message sounds on/off
 *   • mention sounds on/off
 *   • quiet hours (HH:MM 24h start/end)
 *   • channel: push | email | both | none
 *
 * Backed by GET/PUT /api/conv/me/notif-prefs.
 */
import { useEffect, useState } from "react";
import { Bell, BellSlash, MoonStars, FloppyDisk, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";
import { notificationStore } from "@/lib/conv/notificationStore";

const CHANNELS = [
  { v: "both", k: "notif.ch.both" },
  { v: "push", k: "notif.ch.push" },
  { v: "email", k: "notif.ch.email" },
  { v: "none", k: "notif.ch.none" },
];

export default function NotifPrefsPanel({ open, onClose }) {
  const t = useT();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/conv/me/notif-prefs")
      .then((r) => setPrefs(r.data?.prefs || null))
      .catch(() => setPrefs(null));
  }, [open]);

  const save = async () => {
    if (!prefs) return;
    setSaving(true);
    try {
      const r = await api.put("/conv/me/notif-prefs", prefs);
      const fresh = r.data?.prefs || prefs;
      setPrefs(fresh);
      notificationStore.applyPrefsLocally(fresh);
      toast.success(t("notif.saved", "Notification preferences saved"));
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("notif.save_failed", "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-end bg-black/30 p-4" data-testid="notif-prefs-modal" onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-md shadow-xl w-full max-w-sm p-4 mt-12 mr-2"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <Bell size={16} weight="fill" className="text-blue-700" />
          <h2 className="text-sm font-bold tracking-tight">
            {t("notif.title", "Notification preferences")}
          </h2>
          <button onClick={onClose} data-testid="notif-prefs-close"
                  className="ml-auto text-slate-400 hover:text-slate-700">
            <X size={14} weight="bold" />
          </button>
        </div>
        {!prefs ? (
          <p className="text-xs text-slate-500 italic py-6 text-center">
            {t("notif.loading", "Loading…")}
          </p>
        ) : (
          <div className="space-y-3 text-xs">
            <ToggleRow
              icon={<Bell size={12} weight="fill" />}
              testId="notif-msg-sounds"
              label={t("notif.message_sounds", "Message sounds")}
              hint={t("notif.message_sounds_hint", "Soft tick when a new message arrives in any conversation.")}
              checked={prefs.message_sounds}
              onChange={(v) => setPrefs({ ...prefs, message_sounds: v })}
            />
            <ToggleRow
              icon={<BellSlash size={12} weight="fill" />}
              testId="notif-mention-sounds"
              label={t("notif.mention_sounds", "Mention sounds")}
              hint={t("notif.mention_sounds_hint", "Distinct chime when someone @mentions you.")}
              checked={prefs.mention_sounds}
              onChange={(v) => setPrefs({ ...prefs, mention_sounds: v })}
            />

            {/* Quiet hours */}
            <div className="border-t border-slate-100 pt-3">
              <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                <MoonStars size={11} weight="fill" />
                {t("notif.quiet_hours", "Quiet hours")}
              </div>
              <p className="text-[10px] text-slate-500 mb-2">
                {t("notif.quiet_hours_hint", "Sounds and push notifications stay silent during this window. Leave blank to disable.")}
              </p>
              <div className="grid grid-cols-2 gap-2">
                <TimeInput testId="notif-quiet-start" label={t("notif.quiet_start", "Start")}
                  value={prefs.quiet_start} onChange={(v) => setPrefs({ ...prefs, quiet_start: v })} />
                <TimeInput testId="notif-quiet-end" label={t("notif.quiet_end", "End")}
                  value={prefs.quiet_end} onChange={(v) => setPrefs({ ...prefs, quiet_end: v })} />
              </div>
            </div>

            {/* Channel */}
            <div className="border-t border-slate-100 pt-3">
              <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-700 mb-1">
                {t("notif.channel", "Delivery channel")}
              </label>
              <select
                data-testid="notif-channel"
                value={prefs.channel}
                onChange={(e) => setPrefs({ ...prefs, channel: e.target.value })}
                className="w-full text-xs border border-slate-300 rounded-sm px-2 py-1.5 focus:border-blue-500 focus:outline-none">
                {CHANNELS.map((c) => (
                  <option key={c.v} value={c.v}>
                    {t(c.k, c.v)}
                  </option>
                ))}
              </select>
            </div>

            <button
              onClick={save}
              disabled={saving}
              data-testid="notif-prefs-save"
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-blue-700 text-white text-xs font-bold uppercase tracking-wider rounded-sm hover:bg-blue-800 disabled:opacity-40">
              <FloppyDisk size={11} weight="fill" />
              {saving ? t("notif.saving", "Saving…") : t("notif.save", "Save preferences")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ToggleRow({ icon, label, hint, checked, onChange, testId }) {
  return (
    <label className="flex items-start gap-2 cursor-pointer" data-testid={`${testId}-row`}>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={testId}
        className="mt-0.5"
      />
      <span className="flex-1 min-w-0">
        <span className="font-bold text-slate-800 inline-flex items-center gap-1">{icon}{label}</span>
        <span className="block text-[10px] text-slate-500 leading-snug">{hint}</span>
      </span>
    </label>
  );
}

function TimeInput({ label, value, onChange, testId }) {
  return (
    <label className="flex flex-col">
      <span className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">{label}</span>
      <input
        type="time"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="text-xs border border-slate-300 rounded-sm px-2 py-1 focus:border-blue-500 focus:outline-none"
      />
    </label>
  );
}
